/**
 * Feature flags for V3 (AI-Native Reconceptualization).
 *
 * Phase 1 of the AI-Native Reconceptualization
 * (docs/UX_V3_AI_NATIVE_RECONCEPTUALIZATION_PLAN.md
 *  § "Phase 1 Round 1 + Round 2 carryover item 2").
 *
 * Round 2 finding: parallel namespace, NOT extension of useV2Flag.
 * This lets v2 rollback proceed without accidentally affecting v3.
 *
 * Server-side registry: app/feature_flags/v3_registry.py (TBD).
 * CI codegen-drift gate keeps server ↔ frontend synced.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";

export const V3_FLAGS = {
  // Phase 1 — briefing-first landing + 6-mode picker
  BRIEFING_LANDING: "ftl.ux.v3.briefing_landing",
  PROJECT_MODE_SELECTOR: "ftl.ux.v3.project_mode_selector",
  // Command Center Phase 1 — Home leads with the ranked "what needs you now"
  // board (portfolio_frontier_service) instead of an unranked project strip.
  // Default OFF: ships dark, flipped per-org after runtime verification.
  RANKED_HOME: "ftl.ux.v3.ranked_home",

  // Command Center — the project page leads with a decision-first board
  // ("N things need you on this project") instead of the legacy records/
  // pipeline overview. Default OFF: ships dark, flipped per-org after runtime
  // verification. Flip OFF to restore ProjectOverview with zero code change.
  PROJECT_COMMAND_CENTER: "ftl.ux.v3.project_command_center",

  // Phase 2 — Iterator backend

  // Phase 2.5 — Iterator Workflow Engine (Patent AB): adaptive workflow
  // decomposition. Default OFF.
  ITERATOR_WORKFLOW_ENGINE: "ftl.ux.v3.iterator_workflow_engine",

  // Update-biased promotion (Bob 2026-06-07): Felix updates an existing entity
  // rather than spawning a near-duplicate; creates only when there's no match.
  // Default ON; flip OFF for create-only promotion.
  ITERATOR_UPDATE_BIASED_PROMOTION: "ftl.ux.v3.iterator_update_biased_promotion",

  // Autonomous continuous finalize (Bob 2026-06-09: "the conversation IS the
  // permission"). When ON, the conversation drives finalize+promotion (no
  // Iterate button). Default ON; KILL-SWITCH (flip OFF) falls back to
  // explicit-commit-only without a deploy.
  ITERATOR_AUTONOMOUS_FINALIZE: "ftl.ux.v3.iterator_autonomous_finalize",

  // Phase 3 — Iterator MVP frontend
  ITERATOR_MVP: "ftl.ux.v3.iterator_mvp",

  // Iterator Figma-grade shell — persistent chrome (toolbar / Layers rail /
  // canvas / Inspector / Felix dock) replacing the collapsed ControlsDock.
  // Default OFF: ships dark, flipped per-org after runtime verification. Flip
  // OFF to restore the v1 IteratorShell unchanged.
  ITERATOR_SHELL_V2: "ftl.ux.v3.iterator_shell_v2",

  // Phase 4 — CrystallizationKernel visualization

  // Phase 5 — Voice + LiveKit + Iterator-coupled meetings + multi-user
  ITERATOR_VOICE: "ftl.ux.v3.iterator_voice",

  // Phase 6 — Shelf sidebar
  SHELF_SIDEBAR: "ftl.ux.v3.shelf_sidebar",

  // Phase 7 — Self-diagnosing UX-friction → auto-PR
  UXFRICTION_AUTOPR: "ftl.ux.v3.uxfriction_autopr",

  // Phase 8 — Cohort rollout (resequenced after Phase 10)

  // Phase 9 — Domain reseller
  // Phase 9 frontend — DomainSelector naming + checkout surface. Defaults
  // OFF (ships dark); gates the /projects/:projectId/domain route and the
  // Iterator ship-it mount point.
  DOMAIN_SELECTOR: "ftl.ux.v3.domain_selector",

  // Phase 10 — Tenant hosting + embedded Felix
  TENANT_HOSTING: "ftl.ux.v3.tenant_hosting",
  EMBEDDED_FELIX: "ftl.ux.v3.embedded_felix",

  // Phase 11 — Operate + Living App certification
  OPERATE_MODE: "ftl.ux.v3.operate_mode",

  // Phase 12 — Distribution / Marketplace / MCP

  // Phase 13 — Mobile native apps

  // Phase 14 — i18n expansion

  // Phase 15 — Broadcast (Marketing Automation)
  BROADCAST_MODE: "ftl.ux.v3.broadcast_mode",

  // Phase 15.5 — Platform substrate

  // Phase 0 — Patent submission pipeline
} as const;

export type V3FlagKey = keyof typeof V3_FLAGS;
export type V3FlagCode = (typeof V3_FLAGS)[V3FlagKey];

/**
 * Fetch the V3 feature flag state for the current org.
 * Returns a record keyed by flag code with boolean values.
 * Defaults to all-false until the flag service responds.
 */
export function useV3Flags(): {
  flags: Record<V3FlagCode, boolean>;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ["v3-flags"],
    queryFn: async () => {
      try {
        const resp = await apiClient.get<{ flags: Record<string, boolean> }>(
          "/feature-flags/v3",
        );
        return resp.flags ?? {};
      } catch {
        return {};
      }
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  const flags = Object.values(V3_FLAGS).reduce(
    (acc, code) => {
      acc[code] = Boolean(query.data?.[code]);
      return acc;
    },
    {} as Record<V3FlagCode, boolean>,
  );

  return { flags, isLoading: query.isLoading };
}

/**
 * Check a single V3 flag. Convenience wrapper around useV3Flags.
 */
export function useV3Flag(code: V3FlagCode): {
  enabled: boolean;
  isLoading: boolean;
} {
  const { flags, isLoading } = useV3Flags();
  return { enabled: flags[code] === true, isLoading };
}
